import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function CaptureVancouverScenicWonders() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "capture-vancouver-scenic-wonders";

  // 2) Access articles from your context
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1": "Stanley Park & City Skyline",
    "section-2": "Capilano & Grouse Mountain",
    "section-3": "Queen Elizabeth Park & Granville Island",
    "section-4": "Hidden Gems & Local Tips",
    faq: "FAQ",
    "local-regulations": "Local Regulations",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://www.destinationvancouver.com/",
      label: "Destination Vancouver – Official Tourism Site",
    },
    {
      url: "https://vancouver.ca/parks-recreation-culture.aspx",
      label: "Vancouver Parks & Recreation",
    },
    {
      url: "https://www.translink.ca/",
      label: "TransLink Journey Planner",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // Provide as many locations as needed. Each location's "id" must match the
  // ref you use in the corresponding <p> or <li> section. Coordinates go in `lat`/`lng`.
  // Make sure these match the text references exactly.
  const locations = [
    {
      id: "stanley-park-seawall",
      lat: 49.29999,
      lng: -123.13999,
      popupText: "Stanley Park",
    },
    {
      id: "capilano-bridge",
      lat: 49.34278,
      lng: -123.11222,
      popupText: "Capilano Suspension Bridge Park",
    },
    {
      id: "grouse-mountain",
      lat: 49.37956,
      lng: -123.08180,
      popupText: "Grouse Mountain",
    },
    {
      id: "queen-elizabeth",
      lat: 49.24172,
      lng: -123.11276,
      popupText: "Queen Elizabeth Park",
    },
    {
      id: "granville-island",
      lat: 49.27262,
      lng: -123.13524,
      popupText: "Granville Island",
    },
    {
      id: "lynn-canyon",
      lat: 49.34374,
      lng: -123.01796,
      popupText: "Lynn Canyon Park",
    },
    {
      id: "whytecliff-park",
      lat: 49.37401,
      lng: -123.28883,
      popupText: "Whytecliff Park",
    },
    {
      id: "lighthouse-park",
      lat: 49.336,
      lng: -123.262,
      popupText: "Lighthouse Park",
    },
    {
      id: "deep-cove",
      lat: 49.32879,
      lng: -122.95018,
      popupText: "Deep Cove / Quarry Rock",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Ultimate Guide to Vancouver's Most Scenic Wonders</title>
        <meta
          name="description"
          content="Discover Vancouver's best photography spots, hidden gems, and must-visit landmarks in this comprehensive guide."
        />
        <meta
          name="keywords"
          content="Vancouver photography spots, hidden gems in Vancouver, best views in Vancouver"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/capture-vancouver-scenic-wonders.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Ultimate Guide to Vancouver's Most Scenic Wonders"
        />
        <meta
          property="og:description"
          content="Discover Vancouver's best photography spots, hidden gems, and must-visit landmarks in this comprehensive guide."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/capture-vancouver-scenic-wonders.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/capture-vancouver-scenic-wonders.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Ultimate Guide to Vancouver's Most Scenic Wonders"
        />
        <meta
          name="twitter:description"
          content="Discover Vancouver's best photography spots, hidden gems, and must-visit landmarks in this comprehensive guide."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/capture-vancouver-scenic-wonders.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="Panoramic view of Vancouver's skyline and natural landscapes"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver, nestled between the Pacific Ocean and the Coast
              Mountains, offers an unparalleled blend of urban energy and
              natural splendor. Whether you're a seasoned photographer or
              simply a nature enthusiast seeking breathtaking views, this
              comprehensive guide will help you capture the best of Vancouver—
              from iconic skyline shots to hidden forest gems. With tips on
              when to visit, which lenses to bring, and local regulations to
              keep in mind, you’ll be fully prepared to explore Vancouver’s
              must-see locations and hidden treasures.
            </p>

            {/* SECTION: Stanley Park & City Skyline */}
            <h2 id="section-1">Stanley Park & City Skyline</h2>
            <p>
              One of the largest urban parks in North America, Stanley Park
              boasts towering evergreens, coastal views, and a scenic seawall
              that frames Vancouver's skyline perfectly. From Brockton Point
              to Prospect Point, you'll find countless angles to capture
              cityscapes against the backdrop of mountains and ocean.
            </p>
            <p
              ref={(el) => setSpotRef("stanley-park-seawall", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Stanley Park</strong>{" "}
              <Marker
                coordY={49.29999}
                coordX={-123.13999}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – Walk or bike along the seawall for stunning waterfront
              vistas, especially at sunrise or sunset, when the skyline glows
              with soft, golden light.
            </p>

            {/* SECTION: Capilano & Grouse Mountain */}
            <h2 id="section-2">Capilano & Grouse Mountain</h2>
            <p>
              Head across the harbor to North Vancouver for some of the
              region’s most thrilling and scenic experiences. The Capilano
              Suspension Bridge offers an adrenaline rush as you cross a
              swaying bridge high above a canyon. Up Grouse Mountain, a short
              gondola ride transports you to panoramic viewpoints with the
              entire city at your feet.
            </p>
            <p
              ref={(el) => setSpotRef("capilano-bridge", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Capilano Suspension Bridge Park</strong>{" "}
              <Marker
                coordY={49.34278}
                coordX={-123.11222}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – Experience the thrill of walking 70m above the river below,
              surrounded by dense forest.
            </p>
            <p
              ref={(el) => setSpotRef("grouse-mountain", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Grouse Mountain</strong>{" "}
              <Marker
                coordY={49.37956}
                coordX={-123.0818}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – "The Peak of Vancouver" offers gondola rides, hiking trails,
              and sweeping views of the city, mountains, and ocean.
            </p>

            {/* SECTION: Queen Elizabeth Park & Granville Island */}
            <h2 id="section-3">Queen Elizabeth Park & Granville Island</h2>
            <p>
              For panoramic skyline shots in a lush garden setting, Queen
              Elizabeth Park stands out. Meanwhile, Granville Island provides
              a unique vantage across False Creek, featuring reflections of
              Vancouver’s skyscrapers and boats gliding by.
            </p>
            <p
              ref={(el) => setSpotRef("queen-elizabeth", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Queen Elizabeth Park</strong>{" "}
              <Marker
                coordY={49.24172}
                coordX={-123.11276}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – The highest point in Vancouver, it offers 360° views of the
              city skyline against the North Shore Mountains.
            </p>
            <p
              ref={(el) => setSpotRef("granville-island", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Granville Island</strong>{" "}
              <Marker
                coordY={49.27262}
                coordX={-123.13524}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – Renowned for its Public Market and artsy vibe, this waterfront
              spot provides a postcard-perfect view of downtown Vancouver
              across False Creek, especially at sunset.
            </p>

            {/* SECTION: Hidden Gems & Local Tips */}
            <h2 id="section-4">Hidden Gems & Local Tips</h2>
            <p>
              Beyond the popular attractions, Vancouver is home to tranquil
              parks and secret spots where you can avoid the crowd and immerse
              yourself in nature. Whether you're exploring rugged shorelines
              or ancient forests, these lesser-known locations offer equally
              impressive photo ops.
            </p>
            <p
              ref={(el) => setSpotRef("lynn-canyon", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Lynn Canyon Park</strong>{" "}
              <Marker
                coordY={49.34374}
                coordX={-123.01796}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – A free alternative to Capilano, with a suspension bridge
              spanning a beautiful canyon, mossy trails, and natural swimming
              holes.
            </p>
            <p
              ref={(el) => setSpotRef("whytecliff-park", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Whytecliff Park</strong>{" "}
              <Marker
                coordY={49.37401}
                coordX={-123.28883}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – Tucked near Horseshoe Bay, featuring rugged shoreline,
              turquoise waters, and an island accessible at low tide.
            </p>
            <p
              ref={(el) => setSpotRef("lighthouse-park", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Lighthouse Park</strong>{" "}
              <Marker
                coordY={49.336}
                coordX={-123.262}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – Home to a historic lighthouse and old-growth forest. Perfect
              for sunsets and coastal panoramas.
            </p>
            <p
              ref={(el) => setSpotRef("deep-cove", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Deep Cove & Quarry Rock</strong>{" "}
              <Marker
                coordY={49.32879}
                coordX={-122.95018}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – A charming waterfront village with a short hike to Quarry
              Rock, rewarding you with a majestic view of the fjord-like
              Indian Arm.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. When is the best time to visit Vancouver for photography?</h3>
              <p>
                Summer brings long days and vibrant sunsets, while fall adds
                colorful foliage. Spring offers cherry blossoms, and winter
                reveals snow-capped mountains. Regardless of season, the golden
                hour around sunrise or sunset provides the most dramatic
                lighting for photographs.
              </p>

              <h3>2. Do I need a car to visit these spots?</h3>
              <p>
                Many top locations are accessible via public transit, such as
                Stanley Park and Granville Island. However, a car can help you
                reach less urban spots like Whytecliff Park or Lighthouse Park
                more easily. Public transport (buses, SeaBus, SkyTrain) is
                reliable for most other attractions.
              </p>

              <h3>3. Can I use a drone at these locations?</h3>
              <p>
                In most Vancouver parks, drone usage is heavily restricted or
                requires special permits due to controlled airspace and
                wildlife protection. Check official guidelines before flying a
                drone to avoid fines or conflicts with local regulations.
              </p>
            </div>

            {/* SECTION: Local Regulations */}
            <h2 id="local-regulations">Local Regulations</h2>
            <p>
              Be mindful of city and park rules. Recreational drone flying is
              largely prohibited within city limits, especially near airports
              and in sensitive park areas. Commercial photography may require
              permits. Always stay on designated paths, respect wildlife, and
              leave no trace of litter. Consult official park or city websites
              for the latest information on closures and guidelines.
            </p>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Vancouver’s diverse landscapes—urban skylines, towering
              evergreens, ocean shores, and mountain peaks—invite visitors to
              explore and capture the city’s breathtaking beauty. Whether
              you’re touring popular landmarks like Stanley Park or venturing
              off the beaten path into hidden gems like Lynn Canyon, remember
              to travel responsibly and appreciate nature’s wonders. Pack your
              camera gear, plan your route, and prepare to be amazed by all
              that Vancouver has to offer.
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}